// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Bom, Stock } from "../stock/index.js"

// @@imports-module
import { AdhesiveCalculator } from "./Adhesive.js"
import { TapeCalculator } from "./Tape.js"

// @@imports-utils
import { imperialToMetric } from "../utils/index.js"

// @@imports-types
import { ADHESIVE_TYPES, Config, TAPE_TYPES } from "../types/index.js"

// @@body
class ExtraCalculator {
    #stock
    #config
    #adhesives
    #tapes
    #bom = new Bom()

    /**
     * 
     * @param {Stock} stock 
     * @param {Config} config 
     * @param {AdhesiveCalculator} adhesiveCalculator
     * @param {TapeCalculator} tapeCalculator
     */
    constructor(stock, config, adhesiveCalculator, tapeCalculator) {
        this.#stock = stock
        this.#config = config
        this.#adhesives = adhesiveCalculator
        this.#tapes = tapeCalculator
    }

    /**
     * 
     * @param {string} code 
     * @param {number} quantity 
     */
    addExtra(code, quantity) {
        // Ignore extra items with 0 quantity.
        if (quantity <= 0) { return }

        // Fetch extra kit/product from code supplied. Throw if not found or if
        // stock is insufficient to cover required quantity.
        const product = this.#stock.getProduct(code)
        if (!product || product.stock < quantity) {
            throw new Error("Insufficient extra stock")
        }

        // Add required quantity of given product including any additional
        // items required for installation.
        const { tapes: { primerBorder } } = this.#config
        for (let i = 0; i < quantity; i++) {
            this.#bom.addLineItem(product, 1)

            // Add any additional flashing tapes and/or primers required to
            // complete the required detail. Note that calls to tape calculator
            // will automatically add primer required.
            let coverage
            switch (code) {
                case "KRT_2286":
                case "KRT_3048":
                    // Add primer required for 4x 500mm long by 12" wide patches
                    // supplied in timber rooflight kit. Note that the plastic
                    // rooflight kit supplies the same length of 9" tape, so
                    // primer coverage is specified to the wider tape.
                    coverage = 4 *
                        (0.5 + primerBorder) *
                        (imperialToMetric({ inches: 12 }) + primerBorder)
                    this.#adhesives.addAdhesive(ADHESIVE_TYPES.primer, coverage)
                    break
                case "DIN_068_330_245":
                case "DIN_100_330_245":
                case "DIN_110_330_245":
                    // Add 300mm of 12" uncured flashing tape required for patch
                    // detail on internal drains.
                    this.#tapes.addTape(0.3, TAPE_TYPES.uncured, {
                        rollWidth: imperialToMetric({ inches: 12 })
                    })
                    break
                case "DHS_100_065_450":
                case "DHL_100_100_450":
                    // Add 500mm of 18" uncured flashing tape required for patch
                    // detail on horizontal drains.
                    this.#tapes.addTape(0.5, TAPE_TYPES.uncured, {
                        rollWidth: imperialToMetric({ inches: 18 })
                    })
                    break
                case "DPS_152_330_180":
                    // Add primer required for a 330mm diameter (radius 165mm)
                    // pipe seal excluding 250mm inner diameter (radius 125mm).
                    coverage = Math.PI * (0.165 + primerBorder) ** 2 -
                        Math.PI * (0.125 - primerBorder) ** 2
                    this.#adhesives.addAdhesive(ADHESIVE_TYPES.primer, coverage)
                    break
                case "XTKIT_000":
                case "XTKIT_001":
                case "KRC_2286":
                case "KRC_3048":
                case "KHD_0100":
                case "KHD_0065":
                case "KID_0068":
                case "KID_0100":
                case "KID_0110":
                case "KPS_0152":
                    // Handel all-inclusive kits which *just* need to be added
                    // to the basket as a single line item (implemented above).
                    break
                default:
                    throw new Error("Extra product not supported")
            }
        }
    }

    get bom() {
        return this.#bom
    }
}

// @@exports
export { ExtraCalculator }
